import React, { useEffect } from 'react';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  open: boolean;
  message: string;
  variant?: ToastVariant;
  autoHideDuration?: number;
  onClose: () => void;
}

const variantConfig: Record<ToastVariant, { bgColor: string; borderColor: string; textColor: string; icon: string }> = {
  success: {
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    textColor: 'text-green-800',
    icon: '✓',
  },
  error: {
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    textColor: 'text-red-800',
    icon: '✕',
  },
  warning: {
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    textColor: 'text-yellow-800',
    icon: '⚠',
  },
  info: {
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-800',
    icon: 'ℹ',
  },
};

export const Toast: React.FC<ToastProps> = ({
  open,
  message,
  variant = 'info',
  autoHideDuration = 6000,
  onClose,
}) => {
  useEffect(() => {
    if (open && autoHideDuration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoHideDuration);

      return () => clearTimeout(timer);
    }
  }, [open, autoHideDuration, onClose]);

  if (!open) return null;

  const config = variantConfig[variant];

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-2 duration-300">
      <div
        className={`${config.bgColor} ${config.borderColor} ${config.textColor} border rounded-lg shadow-lg px-4 py-3 pr-10 min-w-[300px] max-w-md`}
        role="alert"
      >
        <div className="flex items-start gap-3">
          <span className="text-xl flex-shrink-0">{config.icon}</span>
          <p className="flex-1">{message}</p>
          <button
            onClick={onClose}
            className={`${config.textColor} hover:opacity-70 absolute top-2 right-2 p-1`}
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
